import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useLang } from "@/i18n/LangContext";
import {
  Calendar as CalendarIcon, Plus, Clock, Phone, Pencil, Trash2,
  ArrowRightLeft, ChevronLeft, ChevronRight, Check, DollarSign, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const inputClass = "bg-black/50 border-white/10 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 text-white placeholder:text-neutral-600 h-12 rounded-lg";

export default function AppointmentsTab({ artists, isMaster, authUser }) {
  const { token } = useAuth();
  const { t } = useLang();
  const headers = { Authorization: `Bearer ${token}` };

  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [editApt, setEditApt] = useState(null);
  const [filterArtist, setFilterArtist] = useState("");
  const [reassignApt, setReassignApt] = useState(null);
  const [reassignArtistId, setReassignArtistId] = useState("");
  const [deleteConfirmApt, setDeleteConfirmApt] = useState(null);
  const [creating, setCreating] = useState(false);
  const [gcalConnected, setGcalConnected] = useState(false);

  const [form, setForm] = useState({
    client_name: "", date: "", time: "12:00", duration: 2,
    phone: "", description: "", price: "", advance_paid: false, advance_amount: ""
  });

  const fetchAppointments = useCallback(() => {
    const start = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).toISOString().split("T")[0];
    const end = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 2, 0).toISOString().split("T")[0];
    axios.get(`${API}/admin/appointments?date_from=${start}&date_to=${end}`, { headers })
      .then(res => setAppointments(res.data))
      .catch(() => {});
  }, [viewMonth, token]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Check Google Calendar status (master admin only)
  useEffect(() => {
    if (!isMaster) return;
    axios.get(`${API}/google/status`, { headers })
      .then(res => setGcalConnected(res.data.connected))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaster]);

  const connectGoogleCalendar = async () => {
    try {
      const redirectUri = `${window.location.origin}/api/google/callback`;
      const res = await axios.get(`${API}/google/connect`, {
        headers,
        params: { redirect_uri: redirectUri },
      });
      window.open(res.data.auth_url, "_blank", "width=500,height=600");
      // Poll for connection status
      const poll = setInterval(async () => {
        const status = await axios.get(`${API}/google/status`, { headers });
        if (status.data.connected) {
          setGcalConnected(true);
          clearInterval(poll);
          toast.success("Google Calendar connected!");
        }
      }, 2000);
      setTimeout(() => clearInterval(poll), 120000);
    } catch (err) {
      toast.error("Failed to connect Google Calendar");
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API}/google/disconnect`, {}, { headers });
      setGcalConnected(false);
      toast.success("Google Calendar disconnected");
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  // Cleanup phones on load
  useEffect(() => {
    axios.post(`${API}/admin/appointments/cleanup-phones`, {}, { headers }).catch(() => {});
  }, []);

  const openNewForm = () => {
    setEditApt(null);
    setForm({ client_name: "", date: selectedDate, time: "12:00", duration: 2, phone: "", description: "", price: "", advance_paid: false, advance_amount: "" });
    setShowForm(true);
  };

  const openEditForm = (apt) => {
    setEditApt(apt);
    setForm({
      client_name: apt.client_name,
      date: apt.date,
      time: apt.time,
      duration: apt.duration,
      phone: apt.phone || "",
      description: apt.description || "",
      price: apt.price ?? "",
      advance_paid: apt.advance_paid || false,
      advance_amount: apt.advance_amount || ""
    });
    setShowForm(true);
  };

  const submitForm = async () => {
    if (!form.client_name.trim() || !form.date || !form.time) {
      toast.error("Please fill in client name, date, and time"); return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        duration: parseFloat(form.duration) || 2,
        price: form.price !== "" ? parseFloat(form.price) || 0 : null,
        advance_amount: form.advance_paid ? parseFloat(form.advance_amount) || 0 : null,
        phone: form.phone.trim() || null
      };
      if (editApt) {
        await axios.put(`${API}/admin/appointments/${editApt.id}`, payload, { headers });
        toast.success("Appointment updated");
      } else {
        await axios.post(`${API}/admin/appointments`, payload, { headers });
        toast.success("Appointment created");
      }
      setShowForm(false);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save appointment");
    } finally {
      setCreating(false);
    }
  };

  const deleteAppointment = async () => {
    if (!deleteConfirmApt) return;
    try {
      await axios.delete(`${API}/admin/appointments/${deleteConfirmApt.id}`, { headers });
      toast.success("Appointment deleted");
      setDeleteConfirmApt(null);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const reassignAppointment = async () => {
    if (!reassignArtistId) return;
    try {
      await axios.put(`${API}/admin/appointments/${reassignApt.id}/reassign`, { artist_id: reassignArtistId }, { headers });
      toast.success("Appointment reassigned");
      setReassignApt(null);
      setReassignArtistId("");
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reassign");
    }
  };

  // Calendar helpers
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).getDay();
  const adjustedFirst = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday start
  const monthName = viewMonth.toLocaleString("default", { month: "long", year: "numeric" });

  const getAptsForDate = (date) => {
    let filtered = appointments.filter(a => a.date === date);
    if (isMaster && filterArtist) filtered = filtered.filter(a => a.artist_id === filterArtist);
    return filtered;
  };

  const selectedApts = useMemo(
    () => [...getAptsForDate(selectedDate)].sort((a, b) => a.time.localeCompare(b.time)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointments, selectedDate, filterArtist, isMaster]
  );
  const today = new Date().toISOString().split("T")[0];

  const prevMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  const nextMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  return (
    <div className="space-y-6" data-testid="appointments-tab">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">{t("apt_title")}</h3>
            {isMaster && (gcalConnected ? (
              <button onClick={disconnectGoogleCalendar} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-[10px] text-emerald-400 font-semibold hover:bg-emerald-500/20 transition-colors" data-testid="gcal-connected">
                <Check className="w-3 h-3" /> Google Calendar
              </button>
            ) : (
              <button onClick={connectGoogleCalendar} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[10px] text-neutral-400 font-semibold hover:bg-white/10 transition-colors" data-testid="gcal-connect">
                <CalendarIcon className="w-3 h-3" /> Connect Google
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isMaster && (
            <select
              value={filterArtist}
              onChange={(e) => setFilterArtist(e.target.value)}
              className="bg-black/50 border border-white/10 text-neutral-300 text-xs rounded-lg h-11 px-3 focus:border-purple-500/50 outline-none flex-1 min-w-0"
              data-testid="apt-filter-artist"
            >
              <option value="">{t("apt_all_artists")}</option>
              {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              <option value={authUser?.id}>{t("clients_master")}</option>
            </select>
          )}
          <Button onClick={openNewForm} className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold border border-purple-400 h-11 px-5 shrink-0" data-testid="apt-new-btn">
            <Plus className="w-4 h-4 mr-1" /> {t("apt_new")}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-4">
        {/* Calendar Grid */}
        <div className="glass-card rounded-xl p-4 sm:p-5" data-testid="apt-calendar">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-3 hover:bg-white/5 rounded-lg transition-colors" data-testid="apt-prev-month">
              <ChevronLeft className="w-5 h-5 text-neutral-400" />
            </button>
            <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-300">{monthName}</h4>
            <button onClick={nextMonth} className="p-3 hover:bg-white/5 rounded-lg transition-colors" data-testid="apt-next-month">
              <ChevronRight className="w-5 h-5 text-neutral-400" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
              <div key={d} className="text-center text-[10px] uppercase tracking-wider text-neutral-600 py-1">{d}</div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
            {[...Array(adjustedFirst)].map((_, i) => <div key={`e-${i}`} />)}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const dateStr = `${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const apts = getAptsForDate(dateStr);
              const isSelected = dateStr === selectedDate;
              const isToday = dateStr === today;
              return (
                <button
                  key={day}
                  onClick={() => setSelectedDate(dateStr)}
                  className={`relative aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-all duration-200 min-h-[40px]
                    ${isSelected ? "bg-purple-600/30 border border-purple-500/50 text-white" : "hover:bg-white/5 text-neutral-400"}
                    ${isToday && !isSelected ? "ring-1 ring-pink-500/40" : ""}
                  `}
                  data-testid={`apt-day-${dateStr}`}
                >
                  <span className={`${isToday ? "text-pink-400 font-bold" : ""}`}>{day}</span>
                  {apts.length > 0 && (
                    <div className="flex gap-0.5 mt-0.5">
                      {apts.slice(0, 3).map((apt) => (
                        <div key={apt.id} className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      ))}
                      {apts.length > 3 && <span className="text-[8px] text-purple-400">+</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Day Detail */}
        <div className="glass-card rounded-xl p-4 sm:p-5 max-h-[400px] lg:max-h-[500px] overflow-y-auto" data-testid="apt-day-detail">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-bold text-neutral-300">
              {selectedDate === today ? t("apt_today") : new Date(selectedDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </h4>
            <span className="text-xs text-neutral-600">{selectedApts.length} apt{selectedApts.length !== 1 ? "s" : ""}</span>
          </div>

          {selectedApts.length === 0 ? (
            <p className="text-sm text-neutral-600 text-center py-8">{t("apt_no")}</p>
          ) : (
            <div className="space-y-3">
              {selectedApts.map((apt) => (
                <div key={apt.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-lg hover:border-purple-500/20 transition-colors" data-testid={`apt-card-${apt.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-purple-400 shrink-0" />
                        <span className="text-xs text-purple-300 font-mono font-bold">{apt.time}</span>
                        <span className="text-[10px] text-neutral-600">({apt.duration}h)</span>
                      </div>
                      <p className="text-sm font-semibold text-white mt-1 truncate">{apt.client_name}</p>
                      {isMaster && <p className="text-[10px] text-neutral-500 mt-0.5">{apt.artist_name}</p>}
                      {apt.description && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{apt.description}</p>}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {apt.phone && !apt.phone_hidden && (
                          <span className="text-[10px] text-neutral-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {apt.phone}
                          </span>
                        )}
                        {apt.phone_hidden && (
                          <span className="text-[10px] text-neutral-600 italic">{t("apt_phone_hidden")}</span>
                        )}
                        {apt.advance_paid && (
                          <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> {apt.advance_amount ? `${apt.advance_amount} RON` : t("apt_advance")}
                          </span>
                        )}
                        {apt.price != null && apt.price > 0 && (
                          <span className="text-[10px] text-neutral-300 flex items-center gap-1">
                            <DollarSign className="w-3 h-3 text-neutral-400" />
                            {t("apt_price") || "Preț"}: <strong className="text-white font-semibold">{apt.price} RON</strong>
                          </span>
                        )}
                        {apt.price != null && apt.price > 0 && (() => {
                          const paid = apt.advance_paid ? (apt.advance_amount || 0) : 0;
                          const rest = Math.max(0, apt.price - paid);
                          if (rest <= 0) return (
                            <span className="text-[10px] text-emerald-300 flex items-center gap-1 font-semibold">
                              <Check className="w-3 h-3" /> {t("apt_paid_full") || "Plătit integral"}
                            </span>
                          );
                          return (
                            <span className="text-[10px] text-purple-300 flex items-center gap-1">
                              {t("apt_remaining") || "Rest"}: <strong className="font-semibold">{rest} RON</strong>
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEditForm(apt)} className="p-2.5 hover:bg-white/10 rounded-lg text-neutral-400 hover:text-purple-300 transition-colors" data-testid={`apt-edit-${apt.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      {isMaster && (
                        <button onClick={() => { setReassignApt(apt); setReassignArtistId(""); }} className="p-2.5 hover:bg-white/10 rounded-lg text-neutral-400 hover:text-cyan-300 transition-colors" data-testid={`apt-reassign-${apt.id}`}>
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => setDeleteConfirmApt(apt)} className="p-2.5 hover:bg-white/10 rounded-lg text-neutral-400 hover:text-red-400 transition-colors" data-testid={`apt-delete-${apt.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#0a0a0a] border border-purple-500/20 text-white max-w-md max-h-[85vh] overflow-y-auto" data-testid="apt-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-purple-400" />
              {editApt ? t("apt_edit") : t("apt_new")}
            </DialogTitle>
            <DialogDescription className="text-neutral-500">
              {editApt ? t("apt_reschedule") : t("apt_create")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_client_name")}</Label>
              <Input value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} placeholder="John Doe" className={inputClass} data-testid="apt-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_date")}</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className={inputClass} data-testid="apt-form-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_time")}</Label>
                <Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} className={inputClass} data-testid="apt-form-time" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_duration")}</Label>
              <Input type="number" step="0.5" min="0.5" max="12" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} className={inputClass} data-testid="apt-form-duration" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_phone")}</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+40 7XX XXX XXX" className={inputClass} data-testid="apt-form-phone" />
              <p className="text-[10px] text-neutral-600">{t("apt_phone_note")}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_description")}</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Sleeve, blackwork, full arm..." className="bg-black/50 border-white/10 focus:border-purple-500/50 text-white placeholder:text-neutral-600 min-h-[80px] rounded-lg" data-testid="apt-form-desc" />
            </div>
            <div className="space-y-3 pt-2 border-t border-white/5">
              <div className="space-y-2">
                <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_price") || "Price"} (RON)</Label>
                <Input
                  type="number" min="0" step="1"
                  value={form.price}
                  onChange={e => setForm({...form, price: e.target.value})}
                  placeholder="800"
                  className={inputClass}
                  data-testid="apt-form-price"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="apt-form-advance-toggle">
                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${form.advance_paid ? "bg-emerald-600 border-emerald-400" : "border-white/20 bg-transparent"}`} onClick={() => setForm({...form, advance_paid: !form.advance_paid})}>
                  {form.advance_paid && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-neutral-300" onClick={() => setForm({...form, advance_paid: !form.advance_paid})}>{t("apt_advance")}</span>
              </label>
              {form.advance_paid && (
                <div className="space-y-2 animate-fade-in-up">
                  <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("apt_advance_amount")} (RON)</Label>
                  <Input type="number" min="0" value={form.advance_amount} onChange={e => setForm({...form, advance_amount: e.target.value})} placeholder="200" className={inputClass} data-testid="apt-form-advance-amount" />
                </div>
              )}
              {form.price !== "" && Number(form.price) > 0 && (
                <div
                  className="flex items-center justify-between px-3 py-2 bg-black/30 border border-white/5 rounded-lg text-xs"
                  data-testid="apt-form-remaining"
                >
                  <span className="text-neutral-500 uppercase tracking-wider">
                    {t("apt_remaining") || "Rest de plată"}
                  </span>
                  <span className="text-purple-300 font-semibold">
                    {Math.max(0, (parseFloat(form.price) || 0) - (form.advance_paid ? (parseFloat(form.advance_amount) || 0) : 0)).toFixed(0)} RON
                  </span>
                </div>
              )}
            </div>
            <Button onClick={submitForm} disabled={creating} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12" data-testid="apt-form-submit">
              <CalendarIcon className="w-4 h-4 mr-2" />
              {(() => {
                if (creating) return t("apt_creating");
                if (editApt) return t("apt_save");
                return t("apt_create");
              })()}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={!!reassignApt} onOpenChange={(open) => { if (!open) { setReassignApt(null); setReassignArtistId(""); } }}>
        <DialogContent className="bg-[#0a0a0a] border border-cyan-500/30 text-white max-w-md" data-testid="apt-reassign-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-cyan-400" /> {t("apt_reassign_title")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 mt-2">{t("apt_reassign_desc")}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {reassignApt && (
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-sm text-white font-semibold">{reassignApt.client_name}</p>
                <p className="text-xs text-neutral-500">{reassignApt.date} {t("apt_time").toLowerCase()} {reassignApt.time} — {reassignApt.artist_name}</p>
              </div>
            )}
            <select value={reassignArtistId} onChange={e => setReassignArtistId(e.target.value)} className="w-full bg-black/50 border border-white/10 text-neutral-300 text-sm rounded-lg h-12 px-3 focus:border-cyan-500/50 outline-none" data-testid="apt-reassign-select">
              <option value="">{t("reassign_select")}</option>
              <option value={authUser?.id}>{t("reassign_master")} ({authUser?.name})</option>
              {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <div className="flex gap-3">
              <Button onClick={() => { setReassignApt(null); setReassignArtistId(""); }} variant="outline" className="flex-1 bg-transparent border-white/10 hover:bg-white/5 text-neutral-300" data-testid="apt-reassign-cancel">{t("scan_cancel")}</Button>
              <Button onClick={reassignAppointment} disabled={!reassignArtistId} className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white border border-cyan-400 disabled:opacity-50" data-testid="apt-reassign-confirm">{t("apt_reassign")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmApt} onOpenChange={(open) => { if (!open) setDeleteConfirmApt(null); }}>
        <DialogContent className="bg-[#0a0a0a] border border-red-500/30 text-white max-w-sm" data-testid="apt-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" /> {t("apt_delete")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 mt-2">
              {deleteConfirmApt && t("apt_delete_confirm", { name: deleteConfirmApt.client_name, date: deleteConfirmApt.date })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setDeleteConfirmApt(null)} variant="outline" className="flex-1 bg-transparent border-white/10 hover:bg-white/5 text-neutral-300" data-testid="apt-delete-cancel">{t("scan_cancel")}</Button>
            <Button onClick={deleteAppointment} className="flex-1 bg-red-600 hover:bg-red-500 text-white border border-red-400" data-testid="apt-delete-confirm">{t("apt_delete")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
